import { useCallback, useEffect, useState } from "react";
import { Plus, Trash, TrendUp, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, formatMoney, getCategoryLabel } from "@/lib/currency";
import Modal from "@/components/Modal";

function txAmountClass(type) {
  return type === "income" ? "text-emerald-700" : "text-red-700";
}
function txBadgeClass(type) {
  return type === "income" ? "bg-emerald-200" : "bg-red-200";
}
function typeBtnClass(type, selected) {
  if (type !== selected) return "bg-white";
  return type === "income" ? "bg-emerald-300" : "bg-red-300";
}

function TransactionRow({ t, onDelete }) {
  return (
    <li className="flex items-center gap-4 p-4" data-testid={`tx-row-${t.id}`}>
      <div className={`w-10 h-10 border-2 border-black rounded-lg flex items-center justify-center brutal-shadow-sm ${txBadgeClass(t.type)}`}>
        {t.type === "income" ? <TrendUp size={18} weight="bold" /> : <TrendDown size={18} weight="bold" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-outfit font-bold truncate">{getCategoryLabel(t.category)}</div>
        <div className="text-xs text-black/50 font-manrope truncate">{t.note || "—"} · {new Date(t.date).toLocaleDateString()}</div>
      </div>
      <div className={`font-outfit font-black text-lg ${txAmountClass(t.type)}`}>
        {t.type === "income" ? "+" : "-"}{formatMoney(t.amount, t.currency)}
      </div>
      <button onClick={() => onDelete(t.id)} className="w-8 h-8 border-2 border-black rounded-lg bg-white hover:bg-red-200 flex items-center justify-center" data-testid={`delete-tx-${t.id}`}>
        <Trash size={14} weight="bold" />
      </button>
    </li>
  );
}

function TransactionModal({ open, onClose, onCreated }) {
  const { currency } = useAuth();
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("food");
  const [ccy, setCcy] = useState(currency);
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [currencies, setCurrencies] = useState([currency]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCcy(currency);
    setType("expense");
    setCategory("food");
    setAmount("");
    setNote("");
  }, [open, currency]);

  useEffect(() => {
    api.get("/currencies")
      .then(({ data }) => setCurrencies(data.map((c) => c.code)))
      .catch((err) => console.error("Failed to load currencies:", err));
  }, []);

  useEffect(() => {
    setCategory(type === "expense" ? "food" : "allowance");
  }, [type]);

  const cats = CATEGORIES[type];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/transactions", {
        type, amount: parseFloat(amount), currency: ccy, category, note,
        date: new Date(date).toISOString(),
      });
      toast.success("Added!");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add transaction" testId="transaction-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="transaction-form">
        <div className="flex gap-2">
          {["expense", "income"].map((t) => (
            <button key={t} type="button" onClick={() => setType(t)}
              className={`brutal-btn flex-1 py-2 text-sm capitalize ${typeBtnClass(t, type)}`}
              data-testid={`type-${t}-btn`}>
              {t}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest block mb-1">Amount</label>
            <input type="number" step="0.01" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)}
              className="brutal-input" placeholder="0.00" data-testid="tx-amount-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest block mb-1">Currency</label>
            <select value={ccy} onChange={(e) => setCcy(e.target.value)} className="brutal-input" data-testid="tx-currency-select">
              {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-1">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="brutal-input" data-testid="tx-category-select">
            {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-1">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="brutal-input" data-testid="tx-date-input" />
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-1">Note</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} className="brutal-input" placeholder="Optional" data-testid="tx-note-input" />
        </div>

        <button type="submit" disabled={saving}
          className="brutal-btn bg-violet-300 hover:bg-violet-400 w-full py-2.5 disabled:opacity-50"
          data-testid="tx-submit-btn">
          {saving ? "Saving…" : "Save transaction"}
        </button>
      </form>
    </Modal>
  );
}

export default function Transactions() {
  const [txs, setTxs] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions");
      setTxs(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? txs : txs.filter((t) => t.type === filter);

  const remove = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      setTxs((t) => t.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tight">Transactions</h1>
          <p className="font-manrope text-black/60 mt-1">Every dollar in, every dollar out.</p>
        </div>
        <button onClick={() => setOpen(true)} className="brutal-btn bg-violet-300 hover:bg-violet-400 px-5 py-2.5 flex items-center gap-2" data-testid="new-transaction-btn">
          <Plus size={18} weight="bold" /> New transaction
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", "income", "expense"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`brutal-btn px-4 py-1.5 text-sm capitalize ${filter === f ? "bg-violet-300" : "bg-white hover:bg-yellow-100"}`}
            data-testid={`filter-${f}-btn`}>
            {f}
          </button>
        ))}
      </div>

      <div className="brutal-card p-0 overflow-hidden">
        {loading && <div className="p-6 font-manrope">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center">
            <div className="font-outfit font-black text-2xl mb-2">No transactions yet</div>
            <p className="font-manrope text-black/50 mb-4">Add your first income or expense to get started.</p>
            <button onClick={() => setOpen(true)} className="brutal-btn bg-violet-300 hover:bg-violet-400 px-5 py-2">Add one</button>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="divide-y-2 divide-black" data-testid="transactions-list">
            {filtered.map((t) => <TransactionRow key={t.id} t={t} onDelete={remove} />)}
          </ul>
        )}
      </div>

      <TransactionModal open={open} onClose={() => setOpen(false)} onCreated={load} />
    </div>
  );
}
